import { apiClient } from '../../utils/api';
import {
  PermissionTemplate,
  CreateTemplateRequest,
  UpdateTemplateRequest,
  Permission
} from '../../types/super-admin';

export interface ListTemplatesResponse {
  templates: PermissionTemplate[];
  systemTemplates: PermissionTemplate[];
  customTemplates: PermissionTemplate[];
}

export interface PermissionValidationResult {
  valid: boolean;
  conflicts: string[];
  warnings: string[];
  suggestions: string[];
}

export class PermissionService {
  private static readonly BASE_PATH = '/super-admin/permissions';

  static async listTemplates(): Promise<ListTemplatesResponse> {
    return apiClient.get<ListTemplatesResponse>(`${this.BASE_PATH}/templates`);
  }

  static async getTemplate(templateId: string): Promise<PermissionTemplate> {
    return apiClient.get<PermissionTemplate>(`${this.BASE_PATH}/templates/${templateId}`);
  }

  static async createTemplate(templateData: CreateTemplateRequest): Promise<PermissionTemplate> {
    return apiClient.post<PermissionTemplate>(`${this.BASE_PATH}/templates`, templateData);
  }

  static async updateTemplate(templateId: string, updates: UpdateTemplateRequest): Promise<PermissionTemplate> {
    return apiClient.put<PermissionTemplate>(`${this.BASE_PATH}/templates/${templateId}`, updates);
  }

  static async deleteTemplate(templateId: string): Promise<void> {
    return apiClient.delete<void>(`${this.BASE_PATH}/templates/${templateId}`);
  }

  static async duplicateTemplate(templateId: string, newName: string): Promise<PermissionTemplate> {
    return apiClient.post<PermissionTemplate>(`${this.BASE_PATH}/templates/${templateId}/duplicate`, { name: newName });
  }

  static async assignTemplate(templateId: string, userIds: string[]): Promise<{ success: string[]; failed: string[] }> {
    return apiClient.post<{ success: string[]; failed: string[] }>(`${this.BASE_PATH}/templates/${templateId}/assign`, { userIds });
  }

  static async getTemplateUsers(templateId: string, page = 1, limit = 20): Promise<{
    users: Array<{
      id: string;
      email: string;
      firstName: string;
      lastName: string;
      tenantId: string;
      tenantName: string;
      assignedAt: string;
    }>;
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  }> {
    return apiClient.get<any>(`${this.BASE_PATH}/templates/${templateId}/users?page=${page}&limit=${limit}`);
  }

  static async validatePermissions(permissions: string[]): Promise<PermissionValidationResult> {
    return apiClient.post<PermissionValidationResult>(`${this.BASE_PATH}/validate`, { permissions });
  }

  static async getAvailablePermissions(): Promise<{
    permissions: Permission[];
    categories: Record<string, Permission[]>;
    descriptions: Record<string, string>;
  }> {
    return apiClient.get<any>(`${this.BASE_PATH}/available`);
  }

  static async getPermissionMatrix(templateId?: string): Promise<{
    template?: PermissionTemplate;
    matrix: Record<string, Record<string, boolean>>;
    categories: string[];
    actions: string[];
  }> {
    const url = templateId 
      ? `${this.BASE_PATH}/matrix?templateId=${templateId}`
      : `${this.BASE_PATH}/matrix`;
    return apiClient.get<any>(url);
  }

  static async updatePermissionMatrix(templateId: string, matrix: Record<string, Record<string, boolean>>): Promise<PermissionTemplate> {
    return apiClient.put<PermissionTemplate>(`${this.BASE_PATH}/templates/${templateId}/matrix`, { matrix });
  }

  static async getTemplateUsageStats(templateId: string): Promise<{
    userCount: number;
    tenantDistribution: Record<string, number>;
    roleDistribution: Record<string, number>;
    recentAssignments: Array<{
      userId: string;
      userEmail: string;
      assignedAt: string;
      assignedBy: string;
    }>;
  }> {
    return apiClient.get<any>(`${this.BASE_PATH}/templates/${templateId}/usage-stats`);
  }

  static async exportTemplate(templateId: string): Promise<{ downloadUrl: string }> {
    return apiClient.get<{ downloadUrl: string }>(`${this.BASE_PATH}/templates/${templateId}/export`);
  }

  static async importTemplate(templateData: any): Promise<PermissionTemplate> {
    return apiClient.post<PermissionTemplate>(`${this.BASE_PATH}/templates/import`, templateData);
  }

  static async compareTemplates(templateId1: string, templateId2: string): Promise<{
    template1: PermissionTemplate;
    template2: PermissionTemplate;
    differences: {
      onlyInTemplate1: string[];
      onlyInTemplate2: string[];
      common: string[];
    };
    recommendations: string[];
  }> {
    return apiClient.get<any>(`${this.BASE_PATH}/templates/compare?template1=${templateId1}&template2=${templateId2}`);
  }

  static async getPermissionAuditHistory(templateId: string, page = 1, limit = 20): Promise<{
    logs: Array<{
      id: string;
      timestamp: string;
      action: string;
      changes: {
        before: string[];
        after: string[];
      };
      performedBy: string;
      affectedUsers: number;
    }>;
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  }> {
    return apiClient.get<any>(`${this.BASE_PATH}/templates/${templateId}/audit-history?page=${page}&limit=${limit}`);
  }

  static async bulkUpdateTemplatePermissions(templateIds: string[], permissions: string[]): Promise<{
    success: string[];
    failed: string[];
    affectedUsers: number;
  }> {
    return apiClient.post<any>(`${this.BASE_PATH}/templates/bulk-update`, {
      templateIds,
      permissions
    });
  }
}